import { IconAlertTriangle } from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import { useSeleccionarProveedorPorRfc } from "@/features/proveedor/hooks/use-seleccionar-proveedor";
import type { ProveedoresPorRfc } from "@/features/proveedor/types";
import { SolicitarAltaCatalogo } from "@/features/solicitudes-catalogo/components/SolicitarAltaCatalogo";
import { Alert, Button, Card, Divider, Group, Loader, Stack, Text } from "@/components/ui";
import { CandidatoButton } from "./CandidatoButton";
import { SeccionRFCHeader } from "./SeccionRFCHeader";

interface Props {
  grupo: ProveedoresPorRfc;
  solicitudId: string;
  soloLectura: boolean;
  numeroSolicitud?: number;
}

/**
 * Sección por RFC emisor · maneja la selección local (qué candidato
 * está activo en la UI) y dispara la mutation al asignar.
 *
 * Estados visuales:
 *   1. Ya asignado · muestra el proveedor seleccionado en texto plano.
 *   2. Solo lectura sin asignar · texto dimmed.
 *   3. Sin candidatos en catálogo · alert amarillo pidiendo al admin
 *      dar de alta el proveedor.
 *   4. Un solo candidato · se asigna AUTOMÁTICAMENTE (decisión 43/57).
 *   5. Varios candidatos · lista de `<CandidatoButton>` + botón Asignar.
 */
export function SeccionRFC({ grupo, solicitudId, soloLectura, numeroSolicitud }: Props) {
  const [seleccionado, setSeleccionado] = useState<number | null>(grupo.proveedorSeleccionado?.numeroProveedor ?? null);
  const asignar = useSeleccionarProveedorPorRfc(solicitudId, grupo.rfcEmisor);
  const yaAsignado = grupo.proveedorSeleccionado !== null;
  const candidatoUnico = grupo.candidatos.length === 1;

  // Autoselección · si hay un único proveedor para el RFC, se asigna solo.
  const autoDisparado = useRef(false);
  useEffect(() => {
    if (!soloLectura && !yaAsignado && candidatoUnico && !autoDisparado.current && !asignar.isError) {
      autoDisparado.current = true;
      asignar.mutate(grupo.candidatos[0].numeroProveedor);
    }
  }, [soloLectura, yaAsignado, candidatoUnico, grupo.candidatos, asignar]);

  const autoEnCurso = candidatoUnico && !yaAsignado && !soloLectura && !asignar.isError;

  return (
    <Card
      padding="lg"
      style={{
        borderColor: yaAsignado ? "var(--color-multiva-400)" : undefined,
        background: yaAsignado ? "color-mix(in srgb, var(--color-multiva-600) 6%, transparent)" : undefined,
      }}
    >
      <Stack gap="md">
        <SeccionRFCHeader grupo={grupo} />
        <Divider />

        {yaAsignado ? (
          <Text size="sm">
            <Text span fw={600}>
              Proveedor seleccionado:{" "}
            </Text>
            #{grupo.proveedorSeleccionado!.numeroProveedor} · {grupo.proveedorSeleccionado!.razonSocial}
          </Text>
        ) : soloLectura ? (
          <Text size="sm" c="dimmed">
            Sin proveedor asignado.
          </Text>
        ) : grupo.candidatos.length === 0 ? (
          <Alert variant="warning" icon={<IconAlertTriangle size={16} stroke={1.7} />}>
            <Stack gap="sm">
              <Text size="sm">
                No hay proveedores en el catálogo con RFC <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11px]">{grupo.rfcEmisor}</code>.
              </Text>
              {/* P38 · el solicitante pide el alta y el admin la recibe en su
                  bandeja de Solicitudes de catálogos. */}
              <SolicitarAltaCatalogo tipo="Proveedor" clave={grupo.rfcEmisor} descripcion={grupo.nombreEmisor} solicitudPagoId={solicitudId} numeroSolicitudPago={numeroSolicitud} />
            </Stack>
          </Alert>
        ) : autoEnCurso ? (
          <Group gap="xs" align="center">
            <Loader size="xs" color="multiva" />
            <Text size="sm" c="dimmed">
              Único proveedor para este RFC · asignando automáticamente…
            </Text>
          </Group>
        ) : (
          <Stack gap="sm">
            <Stack gap={6}>
              {grupo.candidatos.map((p) => (
                <CandidatoButton key={p.numeroProveedor} proveedor={p} seleccionado={seleccionado === p.numeroProveedor} onClick={() => setSeleccionado(p.numeroProveedor)} />
              ))}
            </Stack>
            <Group justify="flex-end">
              <Button size="sm" disabled={seleccionado === null} loading={asignar.isPending} onClick={() => asignar.mutate(seleccionado!)}>
                {asignar.isPending ? "Asignando…" : "Asignar"}
              </Button>
            </Group>
          </Stack>
        )}
      </Stack>
    </Card>
  );
}
